import { useEffect, useMemo, useState } from "react";
import { api, API, authFetch } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, ShieldCheck, Copy, ChevronUp, ChevronDown, GripVertical, Search, Download } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, AllergenTags } from "@/components/ui-bits";
import DataIOToolbar from "@/components/DataIOToolbar";
import SupervisorPinModal from "@/components/SupervisorPinModal";

const empty = {
  name: "",
  product_code: "",
  yield_quantity: 1,
  yield_unit: "loaf",
  ingredients: [],
  instructions: "",
  allergens: [],
};

/**
 * One row in the ingredients editor, wired up to `@dnd-kit/sortable` so
 * it can be dragged into a new position. The whole row participates in
 * pointer activation but only the **grip handle** binds the drag
 * listeners — that way taps on the Select / Number inputs interact
 * normally and don't accidentally start a drag.
 *
 * Visual feedback: row gets a slight lift (opacity + transform) while
 * dragging; the grip turns into a "grabbing" cursor.
 */
function SortableIngredientRow({ ingredient, idx, last, materials, setIng, rmIng, moveIng }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: ingredient._key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-12 gap-2 items-center bg-white rounded-md"
      data-testid={`ingredient-row-${idx}`}
    >
      {/* Drag handle — only this element forwards the sortable
          pointer/keyboard listeners, leaving the inputs interactive. */}
      <button
        type="button"
        ref={undefined}
        {...attributes}
        {...listeners}
        className="col-span-1 h-11 flex items-center justify-center text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing touch-none rounded"
        aria-label={`Drag ingredient ${idx + 1}`}
        data-testid={`drag-ingredient-${idx}`}
        title="Drag to reorder"
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="col-span-4">
        <Select
          value={ingredient.material_id}
          onValueChange={(v) => {
            const mat = materials.find((m) => m.id === v);
            setIng(idx, { material_id: v, unit: mat?.unit || ingredient.unit });
          }}
        >
          <SelectTrigger className="h-11"><SelectValue placeholder="Pick material" /></SelectTrigger>
          <SelectContent>
            {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Input
        className="col-span-3 h-11"
        type="number"
        step="0.01"
        value={ingredient.quantity}
        onChange={(e) => setIng(idx, { quantity: e.target.value })}
        placeholder="Qty"
      />
      <Input
        className="col-span-2 h-11"
        value={ingredient.unit}
        onChange={(e) => setIng(idx, { unit: e.target.value })}
        placeholder="unit"
      />
      {/* Keyboard / accessibility fallback — ↑/↓ buttons keep working
          for operators who'd rather tap than drag. */}
      <div className="col-span-2 flex items-center justify-end gap-1">
        <Button
          size="icon" variant="ghost" type="button"
          disabled={idx === 0}
          onClick={() => moveIng(idx, -1)}
          data-testid={`move-ingredient-up-${idx}`}
          title="Move up"
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button
          size="icon" variant="ghost" type="button"
          disabled={last}
          onClick={() => moveIng(idx, 1)}
          data-testid={`move-ingredient-down-${idx}`}
          title="Move down"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Button
          size="icon" variant="ghost" type="button"
          onClick={() => rmIng(idx)}
          data-testid={`remove-ingredient-${idx}`}
          title="Remove"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function Recipes() {
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  // PIN-gate state: pendingAction = { type: "save"|"delete", payload, run }
  const [pinPrompt, setPinPrompt] = useState(null);

  const load = async () => {
    const [r, m] = await Promise.all([api.get("/recipes"), api.get("/materials")]);
    setRows(r.data);
    setMaterials(m.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alphabetise (case-insensitive) so shop-floor staff can find a recipe
  // quickly, then apply the search filter across name + product code.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...rows].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
    );
    if (!q) return sorted;
    return sorted.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.product_code || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const addIng = () =>
    setForm((f) => ({
      ...f,
      ingredients: [
        ...f.ingredients,
        { _key: `ing-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, material_id: "", quantity: 0, unit: "kg" },
      ],
    }));
  const setIng = (i, patch) => setForm((f) => ({
    ...f,
    ingredients: f.ingredients.map((x, idx) => idx === i ? { ...x, ...patch } : x),
  }));
  const rmIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));

  // Swap ingredient at index `i` with its neighbour at `i + delta` (-1 = up,
  // +1 = down). No-op when the move would land outside the array, so the
  // ↑ button on row 0 and ↓ button on the last row are harmless.
  const moveIng = (i, delta) =>
    setForm((f) => {
      const j = i + delta;
      if (j < 0 || j >= f.ingredients.length) return f;
      const next = [...f.ingredients];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...f, ingredients: next };
    });

  // dnd-kit setup. PointerSensor with a tiny activation distance keeps
  // taps on the row's <Select> / <Input> from accidentally starting a
  // drag; KeyboardSensor adds screen-reader-friendly reorder via
  // Space + arrow keys (focus the grip first).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const oldIdx = f.ingredients.findIndex((x) => x._key === active.id);
      const newIdx = f.ingredients.findIndex((x) => x._key === over.id);
      if (oldIdx < 0 || newIdx < 0) return f;
      return { ...f, ingredients: arrayMove(f.ingredients, oldIdx, newIdx) };
    });
  };

  const startNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      name: r.name, product_code: r.product_code || "",
      yield_quantity: r.yield_quantity, yield_unit: r.yield_unit,
      // Inject a `_key` on every loaded ingredient — drag-and-drop reorder
      // needs a stable id per row that survives index shifts. Recipes saved
      // before this feature shipped won't have one, so generate it here.
      ingredients: (r.ingredients || []).map((i) => ({
        _key: i._key || `ing-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ...i,
      })),
      instructions: r.instructions || "",
      allergens: r.allergens || [],
    });
    setOpen(true);
  };

  // Duplicate an existing recipe as a template for a new one.
  // - Clears the product code (codes are unique per spec).
  // - Appends "(Copy)" so the duplicate can't clash with the source name.
  // - Regenerates `_key` so React row identity in the ingredients list stays stable.
  const startCopy = (r) => {
    setEditId(null);
    setForm({
      name: `${r.name} (Copy)`,
      product_code: "",
      yield_quantity: r.yield_quantity,
      yield_unit: r.yield_unit,
      ingredients: (r.ingredients || []).map((i) => ({
        ...i,
        _key: `ing-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      })),
      instructions: r.instructions || "",
      allergens: r.allergens || [],
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    // auto roll-up allergens from chosen materials
    const allergens = Array.from(new Set(
      form.ingredients.flatMap((i) => materials.find((m) => m.id === i.material_id)?.allergens || [])
    ));
    const body = {
      ...form,
      yield_quantity: Number(form.yield_quantity),
      ingredients: form.ingredients.map(({ _key, ...i }) => ({ ...i, quantity: Number(i.quantity) })),
      allergens,
    };
    // Gate behind a supervisor PIN — anyone can attempt, only admin/dispatcher PINs unlock.
    setPinPrompt({
      title: editId ? "Authorise recipe edit" : "Authorise new recipe",
      description: "Recipes are protected. Ask a supervisor to enter their PIN to save.",
      run: async (supervisor) => {
        try {
          if (editId) await api.patch(`/recipes/${editId}`, body);
          else await api.post("/recipes", body);
          toast.success(`Saved · authorised by ${supervisor.name}`);
          setOpen(false);
          load();
        } catch {
          toast.error("Failed");
        }
      },
    });
  };

  const remove = (id) => {
    if (!confirm("Delete recipe?")) return;
    setPinPrompt({
      title: "Authorise recipe deletion",
      description: "Ask a supervisor to enter their PIN to delete this recipe.",
      run: async (supervisor) => {
        await api.delete(`/recipes/${id}`);
        toast.success(`Deleted · authorised by ${supervisor.name}`);
        load();
      },
    });
  };

  const download = async (r) => {
    // Fetch via authFetch → blob → trigger browser download. Reuses the
    // backend's xlsx serialiser so the file round-trips through import.
    try {
      const resp = await authFetch(`${API}/recipes/${r.id}/export.xlsx`);
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(r.name || "recipe").replace(/[^a-z0-9-]+/gi, "-")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  return (
    <div>
      <PageHeader title="Recipes" eyebrow="Production · Specifications · Supervisor-locked">
        <div className="hidden md:flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-stone-500 font-bold mr-3">
          <ShieldCheck className="h-4 w-4 text-primary" /> Supervisor PIN required
        </div>
        <DataIOToolbar resource="recipes" label="Recipes" onImported={load} />
        <Button className="h-12 px-5 gap-2" onClick={startNew} data-testid="new-recipe-button">
          <Plus className="h-5 w-5" /> New recipe
        </Button>
      </PageHeader>

      <Panel
        title={`Recipes (${filteredRows.length}${search ? ` of ${rows.length}` : ""})`}
        action={
          <div className="relative w-64 max-w-full">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or code…"
              className="h-10 pl-9"
              data-testid="recipes-search"
            />
          </div>
        }
      >
        {filteredRows.length === 0 ? (
          <div className="py-10 text-center text-stone-500" data-testid="recipes-empty">
            {rows.length === 0
              ? "No recipes yet."
              : `No recipes match “${search}”.`}
          </div>
        ) : (
          <table className="w-full" data-testid="recipes-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="py-3">Name</th>
                <th>Code</th>
                <th>Yield</th>
                <th>Ingredients</th>
                <th>Allergens</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id} className="border-t border-border h-16">
                  <td className="font-medium">{r.name}</td>
                  <td className="font-mono text-sm">{r.product_code || "—"}</td>
                  <td className="tnum">{fmt2(r.yield_quantity)} {r.yield_unit}</td>
                  <td className="text-sm text-stone-600">{(r.ingredients || []).length}</td>
                  <td><AllergenTags items={r.allergens} /></td>
                  <td className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => download(r)} title="Download as Excel" data-testid={`download-recipe-${r.id}`}><Download className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => startCopy(r)} title="Duplicate as template" data-testid={`copy-recipe-${r.id}`}><Copy className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => startEdit(r)} data-testid={`edit-recipe-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-recipe-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="recipe-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "New"} recipe</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Recipe name</Label><Input className="h-12 mt-1" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="recipe-name" /></div>
              <div><Label>Product code</Label><Input className="h-12 mt-1" value={form.product_code} onChange={(e)=>setForm({...form, product_code: e.target.value})} /></div>
              <div><Label>Yield quantity</Label><Input type="number" step="0.01" className="h-12 mt-1" value={form.yield_quantity} onChange={(e)=>setForm({...form, yield_quantity: e.target.value})} data-testid="recipe-yield" /></div>
              <div><Label>Yield unit</Label><Input className="h-12 mt-1" value={form.yield_unit} onChange={(e)=>setForm({...form, yield_unit: e.target.value})} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ingredients</Label>
                <Button size="sm" type="button" variant="outline" onClick={addIng} data-testid="add-ingredient">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={form.ingredients.map((i) => i._key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {form.ingredients.map((i, idx) => (
                      <SortableIngredientRow
                        key={i._key}
                        ingredient={i}
                        idx={idx}
                        last={idx === form.ingredients.length - 1}
                        materials={materials}
                        setIng={setIng}
                        rmIng={rmIng}
                        moveIng={moveIng}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
            <div>
              <Label>Instructions</Label>
              <Textarea rows={4} value={form.instructions} onChange={(e)=>setForm({...form, instructions: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="recipe-submit">Save recipe</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SupervisorPinModal
        open={!!pinPrompt}
        title={pinPrompt?.title}
        description={pinPrompt?.description}
        roles={["admin", "dispatcher"]}
        onClose={() => setPinPrompt(null)}
        onVerified={(sup) => {
          const fn = pinPrompt?.run;
          setPinPrompt(null);
          fn?.(sup);
        }}
      />
    </div>
  );
}
